import { Component, OnInit, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TablePagination,
  TableSearchEvent,
  TableSort,
  TableDateRangeEvent,
} from "../../../../Common/Components/Table/Type/TableType";
import { TeamLeaderListItem } from "../../Dto/TeamLeaderModel";
import { TeamLeaderEffects } from "../../State/TeamLeaderEffect";
import { TeamLeaderState } from "../../State/TeamLeaderState";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { LocationAddress } from "../../../Location/Dto/LocationAddressModel";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-team-leader-home",
  standalone: true,
  imports: [AppTableComponent],
  templateUrl: "./TeamLeaderHomePage.html",
  styleUrl: "./TeamLeaderHomePage.scss",
})
export class TeamLeaderHomePage implements OnInit {
  private readonly teamLeaderEffects = inject(TeamLeaderEffects);
  private readonly router = inject(Router);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly toastEffects = inject(ToastEffects);
  readonly teamLeaderState = inject(TeamLeaderState);

  teamLeadersColumns: TableColumn<TeamLeaderListItem>[] = [
    { key: "id", header: "ID", sortable: true, width: "8%", visibleByDefault: false },
    { key: "name", header: "Name", sortable: true, width: "15%" },
    { key: "email", header: "Email", sortable: true, width: "15%" },
    { key: "phone", header: "Phone", sortable: true, width: "10%" },
    {
      key: "address",
      header: "Address",
      sortable: false,
      width: "28%",
      cellTemplate: (item: TeamLeaderListItem) => this.formatAddress(item.address),
    },
    {
      key: "partners",
      header: "Partners",
      sortable: false,
      width: "20%",
      cellTemplate: (item: TeamLeaderListItem) => item.partners?.map((inst) => inst.name).join(", ") || "-",
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "12%",
      cellTemplate: (item) => formatDateNumeric(item.createdAt),
    },
  ];

  get teamLeadersConfig(): TableConfig<TeamLeaderListItem> {
    return {
      loading: this.teamLeaderState.loading(),
      emptyMessage: "No team leaders found",
      showActions: true,
      showSearch: true,
      searchPlaceholder: "Search team leaders...",
      showCreate: true,
      createButtonLabel: "Create Team Leader",
      createButtonIcon: "person_add",
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by creation date",
      onCreate: () => this.createTeamLeader(),
      actionButtons: [
        {
          icon: "edit",
          label: "Edit",
          handler: (item: TeamLeaderListItem) => this.editTeamLeader(item),
          visible: () => true,
        },
        {
          icon: "delete",
          label: "Delete",
          handler: (item: TeamLeaderListItem) => this.deleteTeamLeader(item),
          visible: () => true,
        },
      ],
    };
  }

  get teamLeadersPagination(): TablePagination {
    return this.teamLeaderState.pagination();
  }

  ngOnInit(): void {
    this.teamLeaderEffects.searchTeamLeaders().catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    // Removed logging
  }

  onPageChange(event: { page: number; search?: string }): void {
    const currentSearchParams = this.teamLeaderState.searchParams();
    this.teamLeaderEffects
      .searchTeamLeaders({ ...currentSearchParams, page: event.page, search: event.search })
      .catch(() => undefined);
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const currentSearchParams = this.teamLeaderState.searchParams();
    this.teamLeaderEffects
      .searchTeamLeaders({ ...currentSearchParams, page: 1, limit: event.limit, search: event.search })
      .catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    // Removed logging
  }

  onSearchChange(event: TableSearchEvent): void {
    this.teamLeaderEffects.searchTeamLeaders({ search: event.term, page: event.page }).catch(() => undefined);
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const currentSearchParams = this.teamLeaderState.searchParams();
    this.teamLeaderEffects
      .searchTeamLeaders({
        ...currentSearchParams,
        page: event.page,
        createdAtFrom: event.dateRange.from,
        createdAtTo: event.dateRange.to,
      })
      .catch(() => undefined);
  }

  createTeamLeader(): void {
    this.router.navigate(["/team-leaders/create"]).catch(console.error);
  }

  editTeamLeader(teamLeader: TeamLeaderListItem): void {
    this.router.navigate(["/team-leaders/edit", teamLeader.id], { state: { teamLeader } }).catch(console.error);
  }

  deleteTeamLeader(teamLeader: TeamLeaderListItem): void {
    this.confirmModalEffects.open({
      title: "Delete team leader?",
      message: `This will permanently delete ${teamLeader.name} and remove their association with ${teamLeader.partners?.length || 0} partner(s).`,
      confirmText: "Delete team leader",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        try {
          await this.teamLeaderEffects.deleteTeamLeader(teamLeader.id);
          this.toastEffects.show("Team leader deleted", "success");
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Failed to delete team leader"), "error");
        }
      },
    });
  }

  private formatAddress(address?: LocationAddress | null): string {
    if (!address) {
      return "-";
    }

    const parts = [
      address.stateProvinceRegion,
      address.countyDistrict,
      address.subdistrictMunicipality,
      address.communeVillage,
      address.line,
      address.zip,
    ].filter((part) => typeof part === "string" && part.trim().length > 0);

    return (() => {
      if (parts.length > 0) {
        return parts.join(", ");
      }
      return "-";
    })();
  }
}
